
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { BlogPost } from '../types';
import { supabase, isSupabaseConfigured } from '../supabaseClient';
import { useLanguage } from './LanguageContext';
import { BLOG_POSTS } from '../constants';

interface BlogContextType {
  posts: BlogPost[];
  isLoading: boolean;
  addPost: (post: Omit<BlogPost, 'id' | 'created_at'>) => Promise<boolean>;
  updatePost: (id: number, updates: Partial<BlogPost>) => Promise<boolean>;
  deletePost: (id: number) => Promise<void>;
  uploadBlogImage: (file: File) => Promise<string | null>;
  getPostById: (id: number) => Promise<BlogPost | null>;
  refreshPosts: () => Promise<void>;
}

const BlogContext = createContext<BlogContextType | undefined>(undefined);

export const BlogProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { lang: uiLang } = useLanguage();

  const fetchPosts = async () => {
    setIsLoading(true);

    // Fallback to mock data if Supabase is not configured
    if (!isSupabaseConfigured) {
      console.log("Running in Mock Mode: Loading static blog posts.");
      const mockPosts = BLOG_POSTS.map(p => ({
        ...p,
        tags: [],
        faqs: [],
        image_prompt: '',
        lang: 'en',
        created_at: new Date().toISOString()
      }));
      setPosts(mockPosts as any);
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('blogs')
        .select('*')
        .order('id', { ascending: false });

      if (error) {
        console.error("Supabase Blog Fetch Error:", error.message, error.details);
        // Fallback to constants on error
        const mockPosts = BLOG_POSTS.map(p => ({ ...p, tags: [], faqs: [], image_prompt: '', lang: 'en' }));
        setPosts(mockPosts as any);
      } else if (data) {
        const mapped = data.map(d => ({
          ...d,
          tags: d.tags || [],
          faqs: d.faqs || [],
          image: d.image_url || 'https://placehold.co/800x600?text=No+Image',
          image_prompt: d.image_prompt || '',
          lang: d.lang || 'en',
          date: new Date(d.created_at || Date.now()).toLocaleDateString(uiLang === 'zh' ? 'zh-TW' : 'en-US', {
            month: 'short', day: '2-digit', year: 'numeric'
          })
        }));
        setPosts(mapped);
      }
    } catch (e: any) {
      console.error("Unexpected Blog Fetch Exception:", e.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const refreshPosts = async () => {
    await fetchPosts();
  };

  const getPostById = async (id: number): Promise<BlogPost | null> => {
    if (!isSupabaseConfigured) {
      const post = BLOG_POSTS.find(p => p.id === id);
      return post ? { ...post, tags: [], faqs: [], lang: 'en' } as any : null;
    }

    const { data, error } = await supabase.from('blogs').select('*').eq('id', id).single();
    if (error) {
      console.error("Get Blog By ID Error:", error.message);
      return null;
    }
    if (!data) return null;
    return {
      ...data,
      tags: data.tags || [],
      faqs: data.faqs || [],
      image: data.image_url || 'https://placehold.co/800x600?text=No+Image',
      image_prompt: data.image_prompt || '',
      lang: data.lang || 'en',
      date: new Date(data.created_at || Date.now()).toLocaleDateString(uiLang === 'zh' ? 'zh-TW' : 'en-US', {
        month: 'short', day: '2-digit', year: 'numeric'
      })
    };
  };

  const uploadBlogImage = async (file: File) => {
    if (!isSupabaseConfigured) return URL.createObjectURL(file);

    const fileExt = file.name.split('.').pop();
    const fileName = `blog-${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
    const { error: uploadError } = await supabase.storage.from('blog-images').upload(fileName, file);
    if (uploadError) {
      console.error("Upload Error:", uploadError.message);
      return null;
    }
    const { data } = supabase.storage.from('blog-images').getPublicUrl(fileName);
    return data.publicUrl;
  };

  const addPost = async (postData: any): Promise<boolean> => {
    if (!isSupabaseConfigured) { alert("Mock Mode: Cannot save."); return false; }

    const payload = {
      title: postData.title,
      content: postData.content,
      excerpt: postData.excerpt,
      image_url: postData.image,
      image_prompt: postData.image_prompt,
      category: postData.category,
      tags: postData.tags || [], 
      faqs: postData.faqs || [],
      lang: postData.lang || uiLang,
      author: postData.author || 'Natuvía Team'
    };

    const { error } = await supabase.from('blogs').insert([payload]);
    if (error) {
      console.error("Supabase Add Blog Error:", error.message, error.details);
      alert(`儲存失敗！\n錯誤原因：${error.message}\n${error.hint || ''}`);
      return false;
    }
    
    await fetchPosts();
    return true;
  };

  const updatePost = async (id: number, updates: any): Promise<boolean> => {
    if (!isSupabaseConfigured) { alert("Mock Mode: Cannot update."); return false; }

    const { error } = await supabase.from('blogs').update({
      title: updates.title,
      content: updates.content,
      excerpt: updates.excerpt,
      image_url: updates.image,
      image_prompt: updates.image_prompt,
      category: updates.category,
      tags: updates.tags || [], 
      faqs: updates.faqs || [],
      lang: updates.lang
    }).eq('id', id);

    if (error) {
      console.error("Supabase Update Blog Error:", error.message);
      alert(`更新失敗！\n錯誤原因：${error.message}`);
      return false;
    }
    
    await fetchPosts();
    return true;
  };

  const deletePost = async (id: number) => {
    if (!isSupabaseConfigured) { alert("Mock Mode: Cannot delete."); return; }
    if (!confirm("Delete this article?")) return;
    const { error } = await supabase.from('blogs').delete().eq('id', id);
    if (error) {
      console.error("Delete Error:", error.message);
    } else {
      fetchPosts();
    }
  };

  return (
    <BlogContext.Provider value={{ posts, isLoading, addPost, updatePost, deletePost, uploadBlogImage, getPostById, refreshPosts }}>
      {children}
    </BlogContext.Provider>
  );
};

export const useBlog = () => {
  const context = useContext(BlogContext);
  if (!context) throw new Error('useBlog must be used within BlogProvider');
  return context;
};
